/**
 * The devices panel (D171): where the account is signed in, and the way to
 * end any of it — plus the sign-in history under a disclosure.
 *
 * @remarks
 * Pure and prop-driven, and wordless: every label and every line arrives
 * already written (the shell's `device-copy.ts`), so the design lane
 * re-cuts the words without touching this. What the component owns is the
 * shape — a list of devices with this one marked and the others each
 * given a button, one button for all the others, and the history folded
 * under a `<details>` so the dialog stays the size of a dialog.
 *
 * @packageDocumentation
 */

/** One device — a session — as the panel shows it. */
export interface DeviceRowView {
	readonly id: string;
	/** The second line: last seen, and the address, as the shell wrote it. */
	readonly detail: string;
	readonly isCurrent: boolean;
	/** The first line: the browser on the platform, as the shell named it. */
	readonly name: string;
}

/** One sign-in as the history shows it. */
export interface SignInRowView {
	readonly id: string;
	readonly line: string;
}

/** The panel's words — the shell's, handed in once. */
export interface DevicesPanelLabels {
	readonly heading: string;
	readonly historyHeading: string;
	/** The one-device button. */
	readonly revoke: string;
	/** The every-other-device button. */
	readonly revokeOthers: string;
	readonly thisDevice: string;
}

/** Props for {@link DevicesPanelComponent}. */
export interface DevicesPanelView {
	readonly devices: readonly DeviceRowView[];
	readonly history: readonly SignInRowView[];
	/** The buttons wait while a door answers. */
	readonly isBusy: boolean;
	readonly labels: DevicesPanelLabels;
	readonly onRevoke: (id: string) => void;
	readonly onRevokeOthers: () => void;
}

/**
 * Render the devices panel.
 *
 * @param props - {@link DevicesPanelView}
 * @returns The panel
 */
export const DevicesPanelComponent = ({ devices, history, isBusy, labels, onRevoke, onRevokeOthers }: DevicesPanelView) => {
	const others = devices.filter((device) => !device.isCurrent);

	return (
		<section aria-labelledby="devices-panel-heading" className="devices-panel">
			<h3 className="devices-panel__heading" id="devices-panel-heading">
				{labels.heading}
			</h3>
			<ul className="devices-panel__list">
				{devices.map((device) => (
					<li className={`devices-panel__device${device.isCurrent ? ' devices-panel__device--current' : ''}`} key={device.id}>
						<div className="devices-panel__lines">
							<span className="devices-panel__name">
								{device.name}
								{device.isCurrent ? <span className="devices-panel__badge"> · {labels.thisDevice}</span> : undefined}
							</span>
							<span className="devices-panel__detail">{device.detail}</span>
						</div>
						{/* This device signs out from the Sign out button above; the others each have their own. */}
						{device.isCurrent ? undefined : (
							<button
								aria-label={`${labels.revoke} — ${device.name}`}
								className="account-dialog__door"
								disabled={isBusy}
								onClick={() => {
									onRevoke(device.id);
								}}
								type="button"
							>
								{labels.revoke}
							</button>
						)}
					</li>
				))}
			</ul>
			{others.length > 1 ? (
				<button className="account-dialog__door" disabled={isBusy} onClick={onRevokeOthers} type="button">
					{labels.revokeOthers}
				</button>
			) : undefined}
			{history.length > 0 ? (
				<details className="devices-panel__history">
					<summary className="devices-panel__summary">{labels.historyHeading}</summary>
					<ul className="devices-panel__list">
						{history.map((signIn) => (
							<li className="devices-panel__sign-in" key={signIn.id}>
								{signIn.line}
							</li>
						))}
					</ul>
				</details>
			) : undefined}
		</section>
	);
};
